import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { ExternalLink, TrendingUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrandifyCaseStudies = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudy, setSelectedStudy] = useState(null);

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  const fetchCaseStudies = async () => {
    try {
      const response = await axios.get(`${API}/case-studies`);
      setCaseStudies(response.data);
    } catch (error) {
      console.error("Error fetching case studies:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] brandify-body">
      <SEO brand="brandify" path="/brandify/case-studies" title="Case Studies" description="Proof of impact: campaigns, growth metrics, and brand transformations by Brandify." />
      <Navigation brand="brandify" />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="heading-font text-5xl md:text-7xl text-white mb-6" data-testid="case-studies-heading">
              PROVEN
              <span className="text-[#1cb5ae]"> RESULTS</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Real brands. Real growth. Real results. See how we've helped businesses scale with AI-driven strategies.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-[#1cb5ae] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : caseStudies.length === 0 ? (
            <div className="text-center py-20" data-testid="no-case-studies">
              <TrendingUp className="w-20 h-20 text-gray-600 mx-auto mb-6" />
              <h3 className="text-2xl text-gray-400 mb-4">No case studies yet</h3>
              <p className="text-gray-500">Check back soon for inspiring success stories.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {caseStudies.map((study, index) => (
                <motion.div
                  key={study.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-effect rounded-2xl overflow-hidden hover:bg-white/10 transition-all cursor-pointer group"
                  onClick={() => setSelectedStudy(study)}
                  data-testid={`case-study-card-${index}`}
                >
                  <div className="p-6">
                    <div className="text-sm text-[#1cb5ae] mb-2">{study.industry}</div>
                    <h3 className="text-2xl font-semibold text-white mb-3">{study.title}</h3>
                    <p className="text-gray-400">{study.client}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Detail Modal */}
      {selectedStudy && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedStudy(null)}
          data-testid="case-study-modal"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            className="bg-[#0A0A0A] rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <button
                onClick={() => setSelectedStudy(null)}
                className="float-right text-gray-400 hover:text-white text-2xl"
                data-testid="close-modal-btn"
              >
                ×
              </button>
              
              <div className="text-sm text-[#1cb5ae] mb-2">{selectedStudy.industry}</div>
              <h2 className="heading-font text-4xl text-white mb-4">{selectedStudy.title}</h2>
              <p className="text-xl text-gray-400 mb-8">{selectedStudy.client}</p>

              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-semibold text-white mb-3">Challenge</h3>
                  <p className="text-gray-300">{selectedStudy.challenge}</p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-white mb-3">Strategy</h3>
                  <p className="text-gray-300">{selectedStudy.strategy}</p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-white mb-3">Execution</h3>
                  <p className="text-gray-300">{selectedStudy.execution}</p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-white mb-3">Tools Used</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedStudy.tools.map((tool, i) => (
                      <span
                        key={i}
                        className="px-4 py-2 bg-[#1cb5ae]/20 text-[#1cb5ae] rounded-full text-sm"
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-white mb-3">Results</h3>
                  <p className="text-gray-300">{selectedStudy.results}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      <Footer brand="brandify" />
    </div>
  );
};

export default BrandifyCaseStudies;